package com.tregz.tremenz.ui.theme

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.graphics.Color
import com.tregz.design.theme.TregzShapes

private val BrandDarkScheme = darkColorScheme(
    primary = TremenzCoralBright,
    onPrimary = TremenzMaroonCrimson,
    primaryContainer = TremenzCrimsonContainerDark,
    onPrimaryContainer = TremenzCoralContainerLight,
    secondary = TremenzCoralBright,
    onSecondary = TremenzMaroonCrimson,
    secondaryContainer = TremenzCrimsonContainerDark,
    onSecondaryContainer = TremenzCoralContainerLight,
    tertiary = TremenzStageGold,
    onTertiary = TremenzStageGoldDark,
    tertiaryContainer = TremenzStageGoldDark,
    onTertiaryContainer = TremenzStageGold,
    error = Color(0xFFFFB4AB),
    onError = Color(0xFF690005),
    errorContainer = Color(0xFF93000A),
    onErrorContainer = Color(0xFFFFDAD6),
    inverseSurface = TremenzOnSurface,
    inverseOnSurface = TremenzBlack,
    inversePrimary = TremenzDayRed,
    background = TremenzBlack,
    onBackground = TremenzOnSurface,
    surface = TremenzBlack,
    onSurface = TremenzOnSurface,
    surfaceVariant = TremenzPlum,
    onSurfaceVariant = TremenzOnSurfaceVariant,
    surfaceContainerLowest = TremenzBlack,
    surfaceContainerLow = TremenzCard,
    surfaceContainer = TremenzCard,
    surfaceContainerHigh = TremenzCharcoal,
    surfaceContainerHighest = TremenzPlum,
    outline = TremenzOutline,
    outlineVariant = Color(0xFF484848)
)

private val BrandLightScheme = lightColorScheme(
    primary = TremenzDayRed,
    onPrimary = Color.White,
    primaryContainer = TremenzDayRedContainer,
    onPrimaryContainer = TremenzDayOnRedContainer,
    secondary = TremenzDaySalmon,
    onSecondary = Color.White,
    secondaryContainer = TremenzDaySalmonContainer,
    onSecondaryContainer = TremenzDayOnSalmonContainer,
    tertiary = TremenzDayGold,
    onTertiary = Color.White,
    tertiaryContainer = TremenzDayGoldContainer,
    onTertiaryContainer = TremenzDayOnGoldContainer,
    error = Color(0xFFBA1A1A),
    onError = Color.White,
    errorContainer = Color(0xFFFFDAD6),
    onErrorContainer = Color(0xFF93000A),
    inverseSurface = TremenzDayInverseSurface,
    inverseOnSurface = TremenzDayInverseOnSurface,
    inversePrimary = TremenzCoralBright,
    background = TremenzDayCream,
    onBackground = TremenzDayOnCream,
    surface = TremenzDayCream,
    onSurface = TremenzDayOnCream,
    surfaceVariant = TremenzDayBlush,
    onSurfaceVariant = TremenzDayOnBlush,
    surfaceContainerLowest = Color.White,
    surfaceContainerLow = TremenzDayContainerLow,
    surfaceContainer = TremenzDayContainer,
    surfaceContainerHigh = TremenzDayContainerHigh,
    surfaceContainerHighest = TremenzDayContainerHighest,
    outline = TremenzDayOutline,
    outlineVariant = TremenzDayOutlineVariant
)

/**
 * The wallpaper-derived scheme, where the platform offers one (Android 12+). Returns null when the
 * target has no dynamic color, so the theme falls back to the bundled schemes.
 */
@Composable
expect fun dynamicColorScheme(darkTheme: Boolean): ColorScheme?

@Composable
fun TregzTheme(
    // Night is the Tremenz brand identity and the Android app keeps it; the web host passes the
    // system day or night setting instead.
    darkTheme: Boolean = true,
    // Null hides the toolbar's day and night toggle, which only a local web build offers for debugging.
    onDarkThemeChange: ((Boolean) -> Unit)? = null,
    // Wallpaper-derived color pulls Android away from that shared identity, so it stays opt-in.
    dynamicColor: Boolean = false,
    content: @Composable () -> Unit
) {
    val dynamic = if (dynamicColor) dynamicColorScheme(darkTheme) else null
    val colorScheme = dynamic
        ?: if (darkTheme) BrandDarkScheme else BrandLightScheme

    val themeSwitch = ThemeSwitch(
        isNight = darkTheme,
        onToggle = onDarkThemeChange?.let { change -> { change(!darkTheme) } }
    )

    CompositionLocalProvider(LocalThemeSwitch provides themeSwitch) {
        MaterialTheme(
            colorScheme = colorScheme,
            typography = tremenzTypography(),
            shapes = TregzShapes,
            content = content
        )
    }
}
